/**
 * @file enumMasterService.ts
 * @brief Frontend API client for enum_master dropdown values.
 * @author Nestbotics
 * @date 2026-06-19
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from './endpoints';

function unwrapResponse(response: { data?: { success?: boolean; data?: unknown } }): unknown {
  if (response?.data?.success === true && 'data' in response.data) {
    return response.data.data;
  }
  return response?.data;
}

export interface EnumMasterItem {
  id: string;
  moduleName: string;
  keyName: string;
  keyValue: string;
  displayLabel: string;
  sortOrder: number;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export const TASK_CATEGORY_MODULE = 'task';
export const TASK_CATEGORY_KEY = 'task_category';

export const LEAVE_TYPE_MODULE = 'leave';
export const LEAVE_TYPE_KEY = 'leave_type';

export const enumMasterService = {
  /**
   * @brief Fetches active enum values for a module/key pair.
   * @param moduleName Module namespace such as task.
   * @param keyName Enum key such as task_category.
   * @param includeInactive When true, inactive values are included.
   * @return Ordered enum rows from enum_master.
   */
  async list(
    moduleName: string,
    keyName: string,
    includeInactive = false
  ): Promise<EnumMasterItem[]> {
    const response = await apiClient.get(API_ENDPOINTS.ENUM_MASTER.LIST(moduleName, keyName), {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return unwrapResponse(response) as EnumMasterItem[];
  },

  /**
   * @brief Creates a new enum value. Requires manager role or above.
   * @param payload Creation payload.
   * @return Created enum row.
   */
  async create(payload: {
    moduleName: string;
    keyName: string;
    keyValue?: string;
    displayLabel: string;
    sortOrder?: number;
  }): Promise<EnumMasterItem> {
    const response = await apiClient.post(API_ENDPOINTS.ENUM_MASTER.BASE, payload);
    return unwrapResponse(response) as EnumMasterItem;
  },

  /**
   * @brief Updates an enum value. Requires manager role or above.
   * @param enumId Primary key of the enum row.
   * @param updates Partial update payload.
   * @return Updated enum row.
   */
  async update(
    enumId: string,
    updates: { displayLabel?: string; sortOrder?: number; isActive?: boolean }
  ): Promise<EnumMasterItem> {
    const response = await apiClient.patch(API_ENDPOINTS.ENUM_MASTER.BY_ID(enumId), updates);
    return unwrapResponse(response) as EnumMasterItem;
  },

  /**
   * @brief Soft-deletes an enum value. Requires manager role or above.
   * @param enumId Primary key of the enum row.
   * @return Deactivated enum row.
   */
  async deactivate(enumId: string): Promise<EnumMasterItem> {
    const response = await apiClient.delete(API_ENDPOINTS.ENUM_MASTER.BY_ID(enumId));
    return unwrapResponse(response) as EnumMasterItem;
  },

  /**
   * @brief Convenience helper for task category dropdown options.
   * @param includeInactive When true, inactive categories are included.
   * @return Task category enum rows.
   */
  async listTaskCategories(includeInactive = false): Promise<EnumMasterItem[]> {
    return this.list(TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY, includeInactive);
  },

  /**
   * @brief Convenience helper for leave type dropdown options.
   * @param includeInactive When true, inactive leave types are included.
   * @return Leave type enum rows.
   */
  async listLeaveTypes(includeInactive = false): Promise<EnumMasterItem[]> {
    return this.list(LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY, includeInactive);
  },
};

export default enumMasterService;
